import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ChevronLeft,
  ChevronRight,
  Trophy,
  Users,
  MapPin,
  ShoppingBag,
  CalendarDays,
  User as UserIcon,
  Settings,
  Clock,
  ImageOff,
} from 'lucide-react';
import listingService, { ListingEntry } from '../../services/listingService';
import communityService from '../../services/communityService';

const ACCENT = '#3fced1';

type NeuType = 'tournament' | 'club' | 'pub' | 'shop' | 'series' | 'profile' | 'setup';

interface NeuItem {
  key: string;
  title: string;
  type: NeuType;
  imageUrl: string | null;
  overlayUrl: string | null;
  overlayCircle: boolean;
  location: string | null;
  subtitle: string | null;
  createdAt: number;
  route: string | null;
}

const TYPE_META: Record<NeuType, { label: string; Icon: typeof Trophy }> = {
  tournament: { label: 'Turnier', Icon: Trophy },
  club: { label: 'Verein', Icon: Users },
  pub: { label: 'Spielstätte', Icon: MapPin },
  shop: { label: 'Dart-Shop', Icon: ShoppingBag },
  series: { label: 'Serie', Icon: CalendarDays },
  profile: { label: 'Spieler', Icon: UserIcon },
  setup: { label: 'Dart Setup', Icon: Settings },
};

const timeAgo = (createdAtMs: number): string => {
  if (!createdAtMs) return '';
  const days = Math.floor((Date.now() - createdAtMs) / (1000 * 60 * 60 * 24));
  if (days <= 0) return 'Heute';
  if (days === 1) return 'Vor 1 Tag';
  return `Vor ${days} Tagen`;
};

/**
 * "Neu bei DartCloud" – horizontaler Slider mit den neuesten Einträgen,
 * Profilen und Setups. Port der Flutter NewEntriesSection / _NeuContentSlider.
 */
const NeuBeiDartCloudSection: React.FC = () => {
  const navigate = useNavigate();
  const scrollRef = useRef<HTMLDivElement>(null);
  const [items, setItems] = useState<NeuItem[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      const [entries, profilesRes, setupsRes] = await Promise.all([
        listingService.getRecentEntries(20).catch(() => [] as ListingEntry[]),
        communityService.getRecentProfiles({ limit: 10, sort: 'newest' }).catch(() => null),
        communityService.getAllSetups({ limit: 10, sort: 'newest' }).catch(() => null),
      ]);

      const listingItems: NeuItem[] = entries.map((e) => ({
        key: `${e.type}-${e.id}`,
        title: e.name,
        type: e.type,
        imageUrl: e.image,
        overlayUrl: e.logo,
        overlayCircle: false,
        location: e.location && e.location !== 'Unbekannt' ? e.location : null,
        subtitle: null,
        createdAt: e.createdAt,
        route: listingService.routeFor(e),
      }));

      const profileItems: NeuItem[] = (profilesRes?.profiles ?? [])
        .filter((p) => p.image)
        .map((p) => ({
          key: `profile-${p.id}`,
          title: p.username,
          type: 'profile' as NeuType,
          imageUrl: p.headerImage ? communityService.buildThumbnailUrl(p.headerImage, 400) : null,
          overlayUrl: communityService.buildThumbnailUrl(p.image, 100),
          overlayCircle: true,
          location: null,
          subtitle: p.bio ? (p.bio.length > 80 ? `${p.bio.slice(0, 80)}…` : p.bio) : null,
          createdAt: (p.memberSince ?? 0) * (p.memberSince && p.memberSince < 1e12 ? 1000 : 1),
          route: `/spieler/${p.username}`,
        }));

      const setupItems: NeuItem[] = (setupsRes?.setups ?? [])
        .filter((s) => s.image)
        .map((s) => ({
          key: `setup-${s.id}`,
          title: s.title,
          type: 'setup' as NeuType,
          imageUrl: communityService.buildThumbnailUrl(s.image, 400),
          overlayUrl: null,
          overlayCircle: false,
          location: null,
          subtitle:
            s.subtitle ||
            (s.description
              ? s.description.length > 60
                ? `${s.description.slice(0, 60)}…`
                : s.description
              : null),
          createdAt: s.createdAt,
          route: `/setup/${s.id}`,
        }));

      // Mischen: zuerst nach Datum, profile/setups eingestreut
      const merged = [...listingItems, ...profileItems, ...setupItems].sort(
        (a, b) => b.createdAt - a.createdAt,
      );

      if (!cancelled) {
        setItems(merged.slice(0, 16));
        setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, []);

  const scrollBy = (delta: number) => {
    scrollRef.current?.scrollBy({ left: delta, behavior: 'smooth' });
  };

  const skeletons = useMemo(() => Array.from({ length: 5 }), []);

  return (
    <section className="mx-auto max-w-7xl px-4 py-12 sm:px-6">
      <div className="mb-6">
        <h2 className="section-heading text-2xl font-bold tracking-tight text-white sm:text-3xl">
          Neu bei DartCloud
        </h2>
        <p className="mt-1.5 text-sm text-gray-400">Die neuesten Einträge auf einen Blick</p>
      </div>

      <div className="relative">
        {/* Scroll-Buttons (Desktop) */}
        {!loading && items.length > 0 && (
          <>
            <button
              type="button"
              aria-label="Zurück"
              onClick={() => scrollBy(-320)}
              className="absolute left-0 top-1/2 z-10 hidden -translate-y-1/2 items-center justify-center rounded-full border border-zinc-700 bg-black/80 p-2 text-white shadow-lg backdrop-blur transition-colors hover:bg-zinc-800 sm:flex"
            >
              <ChevronLeft size={20} />
            </button>
            <button
              type="button"
              aria-label="Weiter"
              onClick={() => scrollBy(320)}
              className="absolute right-0 top-1/2 z-10 hidden -translate-y-1/2 items-center justify-center rounded-full border border-zinc-700 bg-black/80 p-2 text-white shadow-lg backdrop-blur transition-colors hover:bg-zinc-800 sm:flex"
            >
              <ChevronRight size={20} />
            </button>
          </>
        )}

        <div
          ref={scrollRef}
          className="flex gap-4 overflow-x-auto scroll-smooth pb-2 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
        >
          {loading
            ? skeletons.map((_, i) => (
                <div
                  key={i}
                  className="h-[320px] w-[280px] shrink-0 animate-pulse rounded-2xl border border-zinc-800 bg-zinc-900"
                />
              ))
            : items.map((item) => (
                <NeuCard key={item.key} item={item} onClick={() => item.route && navigate(item.route)} />
              ))}

          {!loading && items.length === 0 && (
            <div className="flex h-[200px] w-full items-center justify-center text-sm text-gray-500">
              Keine Einträge gefunden
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

const NeuCard: React.FC<{ item: NeuItem; onClick: () => void }> = ({ item, onClick }) => {
  const [imgError, setImgError] = useState(false);
  const { label, Icon } = TYPE_META[item.type];
  const showImage = item.imageUrl && !imgError;

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={!item.route}
      className="card-lift group flex h-[320px] w-[280px] shrink-0 flex-col overflow-hidden rounded-2xl border border-zinc-800 bg-zinc-900 text-left disabled:cursor-default"
    >
      {/* Bild-Header */}
      <div className="relative h-[160px] w-full shrink-0 overflow-hidden bg-zinc-800">
        {showImage ? (
          <img
            src={item.imageUrl as string}
            alt={item.title}
            onError={() => setImgError(true)}
            className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center">
            <ImageOff size={40} className="text-zinc-600" />
          </div>
        )}

        {/* Typ-Badge */}
        <span
          className="absolute left-3 top-3 inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1 text-[11px] font-semibold text-black"
          style={{ backgroundColor: ACCENT }}
        >
          <Icon size={14} /> {label}
        </span>

        {/* NEU-Badge */}
        <span
          className="absolute right-3 top-3 rounded-md px-1.5 py-0.5 text-[9px] font-bold tracking-wider text-black"
          style={{ backgroundColor: ACCENT }}
        >
          NEU
        </span>

        {/* Overlay (Logo / Profilbild) */}
        {item.overlayUrl && (
          <div
            className={`absolute bottom-3 right-3 h-12 w-12 overflow-hidden border-2 border-white bg-white shadow-md ${
              item.overlayCircle ? 'rounded-full' : 'rounded-lg'
            }`}
          >
            <img src={item.overlayUrl} alt="" className="h-full w-full object-cover" />
          </div>
        )}
      </div>

      {/* Inhalt */}
      <div className="flex flex-1 flex-col p-4">
        <h3 className="line-clamp-2 text-base font-bold text-white">{item.title}</h3>

        {item.subtitle && (
          <p className="mt-1 line-clamp-2 text-xs text-gray-400">{item.subtitle}</p>
        )}
        {item.location && (
          <div className="mt-1 flex items-center gap-1 text-xs text-gray-500">
            <MapPin size={13} />
            <span className="truncate">{item.location}</span>
          </div>
        )}

        <div className="mt-auto pt-3">
          <span className="inline-flex items-center gap-1.5 rounded-md bg-zinc-800 px-2.5 py-1 text-[11px] font-medium text-gray-400">
            <Clock size={12} /> {timeAgo(item.createdAt)}
          </span>
        </div>
      </div>
    </button>
  );
};

export default NeuBeiDartCloudSection;
